import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import PriceLevel from './PriceLevel';
import KioskPriceLevel from './KioskPriceLevel';
import { CurrencyContext } from '../../../config/CurrencyContext';
import { getJSFormattedCurrency, toMinorUnit, toDecimals } from '@checkout-h/currency';
import { calculateDiscountedLevelPriceFromSectionLevel } from '../../../selectors/reserved-seating.js';
import { DISCOUNT_TYPE_FLAT, DISCOUNT_TYPE_PERCENT, DISCOUNT_TYPE_FIXED } from '../../../reducers/reservedSeating';
import { percentageFormat } from '@showclix-shared-intl/numberFormatters';
import { TranslationContext } from '../../../../checkout/config/Translations';
import { useFocusTrapContext } from '@checkout-b/checkout/contexts/FocusTrapContext';
import { focusTraps } from '@checkout-b/checkout/contexts/focusTraps';
import DetailsToggle from '@checkout-s/DetailsToggle';
import { getXssFilter } from '@showclix-shared-util/xssFilter';

const PriceLevelInfo = ({ priceLevel }) => {
    const [isDetailsOpen, setIsDetailsOpen] = React.useState(false);

    const toggleDetailsOpen = () => {
        setIsDetailsOpen(s => !s);
    }
    
    if (!priceLevel || (!priceLevel.subheading && !priceLevel.description)) {
        return null;
    }

    return (
        <div>
            {priceLevel.subheading && (
                <div className="price-level-subheading">
                    {priceLevel.subheading}
                </div>
            )}
            {priceLevel.description && (
                <DetailsToggle
                    onClick={toggleDetailsOpen}
                    index={0}
                    aria-expanded={isDetailsOpen}
                    aria-controls={`description_price-level_${priceLevel.price_level_id}`}
                    aria-describedby={`name_price-level_${priceLevel.price_level_id}`}
                />
            )}
            {(isDetailsOpen && priceLevel.description) && (
                <div className="ticket__description ticket__item">
                    <div
                        id={`description_price-level_${priceLevel.price_level_id}`}
                        className="ticket-description__content"
                        name={`description_${priceLevel.price_level_id}`}
                        dangerouslySetInnerHTML={{
                            __html: getXssFilter().process(priceLevel.description)
                        }}
                    />
                </div>
            )}
        </div>
    );
}


const BestAvailableSeatPicker = props => {
  const { reactivateFocusTrap } = useFocusTrapContext();
  useEffect(() => {
    reactivateFocusTrap(focusTraps.TICKET_PICKER);
  }, [])

  const translate = React.useContext(TranslationContext).t;
  const reserveCouponId = props?.reserveCouponUsage?.attributes?.coupon_link_id
        ? props.reserveCouponUsage.attributes.coupon_link_id : null;

  // filter out sold out sections
    const reservedPriceLevels = props.reservedPriceLevels ? Object.values(props.reservedPriceLevels).filter(lvl => +lvl?.transaction_limit > 0) : null;
    const priceLevels = Object.values(props.priceLevels).filter(lvl => +lvl?.transaction_limit > 0).sort((a,b) => {
        let index_a= parseInt(a.position);
        let index_b= parseInt(b.position);
        if (index_a == index_b) {
          return 0;
        }
        return (index_a < index_b) ? -1 : 1;
      }
    );

    const defaultSection = () => {
        if (!props.hideBestAvailable && !props.disableBestAvailable) {
            return 'best_available';
        }
        return priceLevels.length ? priceLevels[0]?.price_level_id : '';
    }

    const currencyConfig = React.useContext(CurrencyContext);
    const [ section, setSection ] = React.useState(defaultSection());

    // on mount, set a quantity for the default section
    React.useEffect(() => {
        props.onSetSectionLevelQuantities({});
        props.onSetQuantities({
            [defaultSection()]: 0,
        });
    }, []);

    if (!priceLevels.length) {
        return null;
    }

    const formatCurrency = amount => {
        return (!currencyConfig) ? '' : getJSFormattedCurrency(+amount, currencyConfig);
    }

    const getLevelById = levelId => {
        let result = null;
        priceLevels.forEach(level => {
            if (level?.price_level_id == levelId) {
                result = level;
            }
        });
        return result;
    }

    const getMinPricedPriceLevel = () => {
        let result = null;
        priceLevels.forEach(level => {
            const levelPrice = +level?.price?.price;
            if (result === null || levelPrice < +result?.price?.price) {
                result = level;
            }
        });
        return result;
    }

    const getMaxPricedPriceLevel = () => {
        let result = null;
        priceLevels.forEach(level => {
            const levelPrice = +level?.price?.price;
            if (result === null || levelPrice > +result?.price?.price) {
                result = level;
            }
        });
        return result;
    }

    const getPriceStringFromLevelObject = (priceLevelObject, inclusiveTotal = false, includeFee = false) => {
        let priceString = formatCurrency(+priceLevelObject.price.price);

        if (includeFee) {
          priceString = priceString + getFeeStringFromLevelObject(priceLevelObject);
        }

        return inclusiveTotal ? formatCurrency(+priceLevelObject.price.price + priceLevelObject.service_fee.price) : priceString;
    }

    const getFeeStringFromLevelObject = priceLevelObject => {
        if (props.discloseFee && !!priceLevelObject?.service_fee?.price) {
          return translate(props.feeVerbiage ? 'checkout__plus_symbol_fee_label' : 'checkout__plus_symbol_service_fee', {amount:formatCurrency(priceLevelObject.service_fee.price)});
        }
        return '';
    }

    const getRangePriceStringFromLevelObjects = (minPriceLevelObject, maxPriceLevelObject, inclusiveTotal = false) => {
      if (!props.discloseFee) {
        return translate('checkout__low_thru_high', {lowAmount:getPriceStringFromLevelObject(minPriceLevelObject), highAmount:getPriceStringFromLevelObject(maxPriceLevelObject)});
      }
        return translate('checkout__low_thru_high', {lowAmount:getPriceStringFromLevelObject(minPriceLevelObject, inclusiveTotal), highAmount:getPriceStringFromLevelObject(maxPriceLevelObject, inclusiveTotal)});
    }

    const getRangeFeeStringFromLevelObjects = (minPriceLevelObject, maxPriceLevelObject, feesIncluded = false) => {
        if (props.discloseFee && !feesIncluded) {
            if (!!maxPriceLevelObject?.service_fee?.price && (minPriceLevelObject.service_fee.price !== maxPriceLevelObject.service_fee.price)) {
                return translate(props.feeVerbiage ? 'checkout__plus_fees_range' : 'checkout__plus_service_fee_range', {lowFee:formatCurrency(minPriceLevelObject.service_fee.price), highFee:formatCurrency(maxPriceLevelObject.service_fee.price)});
            }
            return getFeeStringFromLevelObject(minPriceLevelObject);
        }
        return '';
    }

    const getPriceRangeStringFromLevels = (feeDisclosure = false) => {
        let min = getMinPricedPriceLevel();
        let max = getMaxPricedPriceLevel();
        if (min === null || max === null) {
            return '';
        }
        if (+min?.price?.price === +max?.price?.price) {
          return getPriceStringFromLevelObject(min, props.discloseFee);
        }
        return feeDisclosure ?
            <span>
              <span>({getRangePriceStringFromLevelObjects(min,max)},</span>
              <div>{getRangeFeeStringFromLevelObjects(min,max)})</div>
            </span> :
            getRangePriceStringFromLevelObjects(min,max,props.discloseFee);
    }

    const onChangeSection = e => {
        const newSection = e.target.value;
        props.onSetSectionLevelQuantities(reserveCouponId ? {[newSection]: 0}: {});
        props.onSetQuantities({[newSection]: 0});
        setSection(newSection);
    }

    const getQuantityOptions = (sectionPriceLevel = null) => {
        const quantities = [];
        let max = props.purchaseLimit;

        // use reserve coupon limit if it applies
        if (
            props.reserveCouponUsage?.attributes?.ticket_limit !== undefined &&
            props.reserveCouponUsage.attributes.ticket_limit != null &&
            +props.reserveCouponUsage.attributes.ticket_limit < max
        ) {
            max = +props.reserveCouponUsage.attributes.ticket_limit;
        }

        let min = 0;
        // use PL purchase limits
        if (section != 'best_available') {
            const plObject = getLevelById(section);
            if (plObject) {
                max = Math.min(max, +plObject.transaction_limit);
            }
            min = +plObject?.minimum_purchase_limit;
        } else {
            // if best available, get the min of all price levels
            min = getMinPurchaseLimitForAllLevels();
        }
        if (sectionPriceLevel && sectionPriceLevel?.minimum_purchase_limit !== null && sectionPriceLevel?.minimum_purchase_limit !== undefined) {
            min = +sectionPriceLevel.minimum_purchase_limit;
        }
        for (let i = min; i <= max; i = i + props.incrementBy) {
          quantities.push(i);
        }
        return quantities.reduce((temp, a) => temp + a, 0) < 1 ? [] : quantities;
    }

    const getMinPurchaseLimitForAllLevels = () => {
        let min = null;
        priceLevels.forEach(pl => {
            if (min === null || +pl?.minimum_purchase_limit < min) {
                min = +pl.minimum_purchase_limit;
            }
        });
        return min === null ? 0 : min;
    }

    const getQuantity = () => {
        return Object.values(props.quantities).length ? +Object.values(props.quantities)[0] : 0;
    }

    const getSectionLevelQuantity = sectionLevelId => {
        return props.sectionLevelQuantities[sectionLevelId] ?? 0;
    }

    const getDiscountedPriceForLevel = (priceLevelObject, sectionPriceLevel, inclusiveTotal = false) => {
        const currency = priceLevelObject?.price?.currency ?? 'USD';
        let cost = calculateDiscountedLevelPriceFromSectionLevel(priceLevelObject, sectionPriceLevel);
        let fee = null;
        if (props.discloseFee) {
            Object.keys(props.sectionFeeMap).forEach(plCost => {
                if (cost == +plCost) {
                    fee = +props.sectionFeeMap[plCost];
                }
            });
        }
        if (inclusiveTotal) {
          return `${formatCurrency(cost + fee)}`;
        }

        return fee === null ? [formatCurrency(cost)] : [formatCurrency(cost), formatCurrency(fee)];
    }

    const setSectionLevelQuantityForId = (sectionLevelId, value) => {
        const slq = {...props.sectionLevelQuantities};
        slq[sectionLevelId] = value;
        props.onSetSectionLevelQuantities(slq);
    }

    const quantities = getQuantityOptions();

    const traverseQuantityOptions = (providedQuantities, currentNum, numToApply) => {
        let index = !!providedQuantities.length && providedQuantities[0] === 0 ? 0 : -1;
        if (currentNum && providedQuantities.indexOf(currentNum) > -1) {
            index = providedQuantities.indexOf(currentNum);
        }
        const newIndex = Math.min(Math.max(-1, index + numToApply), providedQuantities.length -1);
        return newIndex === -1 ? 0 : providedQuantities[newIndex];
    }

    const priceObjectPlaceholder = {
        price: 0,
        currency: priceLevels[0]?.price?.currency ?? 'USD',
        currency_symbol: priceLevels[0]?.price?.currency_symbol ?? '$',
    };

    const PlComponent = props.isKioskMode ? KioskPriceLevel : PriceLevel;

    const validReservePlIds = Array.isArray(props?.reserveCouponUsage?.attributes?.valid_reserve_price_level_ids)
        ? props.reserveCouponUsage.attributes.valid_reserve_price_level_ids.map(plId => +plId) : [];

    const getDescriptionForSectionPriceLevel = (secLevelDiscountType, secLevelDiscountAmount) => {
        if (+secLevelDiscountAmount > 0) {
            if (+secLevelDiscountType === DISCOUNT_TYPE_FLAT || +secLevelDiscountType === DISCOUNT_TYPE_FIXED) {
                return translate('checkout__discount_amount', {amount:formatCurrency(toMinorUnit(secLevelDiscountAmount, priceLevels[0]?.price?.currency ?? 'USD'))});
            }
            if (+secLevelDiscountType === DISCOUNT_TYPE_PERCENT) {
                return translate('checkout__discount_amount', {amount:percentageFormat(props.locale, secLevelDiscountAmount)});
            }
        }
        return '';
    }

    const mainQuantityInput = (infoBelowName = '', infoToRightOfName = '', customFeeInfo = '') => {

        let mainQuantityInputTitle = translate('checkout__quantity');
        if (reserveCouponId) {
            mainQuantityInputTitle = translate('checkout__reserved_seating');
        } else if (props.sectionPriceLevels.length) {
          const reserveSeatingLabel = priceLevels[0]?.reserved_seating_label ? priceLevels[0]?.reserved_seating_label : translate('checkout__regular_admission');
          mainQuantityInputTitle = props.defaultPriceLevelName ? props.defaultPriceLevelName : reserveSeatingLabel;
        }

        return (
            <PlComponent
                level={{
                    name: mainQuantityInputTitle,
                    price: {...priceObjectPlaceholder},
                    service_fee: {...priceObjectPlaceholder},
                }}
                quantity={getQuantity()}
                onIncrement={() => {props.onSetQuantities({[section]: traverseQuantityOptions(quantities, getQuantity(), 1)})}}
                onDecrement={() => {props.onSetQuantities({[section]: Math.max(traverseQuantityOptions(quantities, getQuantity(), -1), 0)})}}
                onQuantityInput={e => {props.onSetQuantities({[section]: Math.max(+e.target.value, 0)})}}
                quantities={quantities}
                initialQuantities={quantities}
                disabled={!!props.disabled}
                isPWYW={false}
                customTicketInformationBelowName={infoBelowName}
                customTicketInformationToRightOfName={infoToRightOfName}
                customFeeDisplay={customFeeInfo}
            />
        );
    }

    const selected_pl = getLevelById(section);
    const is_ga = selected_pl && !selected_pl.is_seated;

    return (
            <div className={`best-available-seat-picker ${props.isKioskMode ? 'best-available-seat-picker--kiosk' : ''}`}>
                <div className="best-available-seat-picker__input">
                    {!reserveCouponId && 
                        <div className="ticket__item">
                            <div className="input input__select" style={{marginTop:'12px'}}>
                                <select
                                    className="input__control"
                                    name="section"
                                    onChange={onChangeSection}
                                    value={section}
                                >
                                    {!props.hideBestAvailable && !props.disableBestAvailable && <option value="best_available">{translate('checkout__best_available')}</option>}
                                    {priceLevels.map((level, index) => {
                                        return (
                                            <option value={level?.price_level_id} key={index}>
                                                {`${level?.name ?? translate('checkout__section')} (${getPriceStringFromLevelObject(level, null, props.discloseFee)})`}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                            <PriceLevelInfo priceLevel={priceLevels.find(pl => +pl.price_level_id === +section)} />
                        </div>
                    }
                    {!!props.reservedPriceLevels && Object.keys(props.reservedPriceLevels).length!==0  && !!reserveCouponId && (
                        <div className="ticket__item">
                            <div className="input input__select" style={{marginTop:'12px'}}>
                                <select
                                    className="input__control"
                                    name="section"
                                    onChange={onChangeSection}
                                    value={section}
                                >
                                    {!props.hideBestAvailable && <option value="best_available">{translate('checkout__best_available')}</option>}
                                    {reservedPriceLevels.map((level, index) => {
                                        return (
                                            <option value={level?.price_level_id} key={index}>
                                                {`${level?.name ?? translate('checkout__section')} (${getPriceStringFromLevelObject(level, null, props.discloseFee)})`}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                            <PriceLevelInfo priceLevel={reservedPriceLevels.find(pl => +pl.price_level_id === +section)} />
                        </div>
                    )}

                    {!props.sectionPriceLevels.length && (
                        <div>
                            {mainQuantityInput()}
                        </div>
                    )}

                    {!!props.sectionPriceLevels.length && (
                        <div>
                            {mainQuantityInput(
                                (props.hidePremierePriceDiscount || !!reserveCouponId) ? <div className="ticket__price--total" /> : (
                                    <div>
                                        <div className="ticket__price--total hide-on-medium-screen">
                                            {section === 'best_available' ? getPriceRangeStringFromLevels(false) : getPriceStringFromLevelObject(getLevelById(section), props.discloseFee)}
                                        </div>
                                        {props.discloseFee ? <div className="ticket__price--breakdown">
                                            {section === 'best_available' ?
                                        <span>{getPriceRangeStringFromLevels(props.discloseFee)}</span> :
                                              <span>({getPriceStringFromLevelObject(getLevelById(section), false, true)})</span>}
                                        </div> : ''}
                                    </div>
                                ),
                                (props.hidePremierePriceDiscount || !!reserveCouponId) ? <div className="ticket__price" /> : (
                                    <div className="ticket__price--total">
                                        {section === 'best_available' ? getPriceRangeStringFromLevels() : getPriceStringFromLevelObject(getLevelById(section), props.discloseFee)}
                                    </div>
                                ),
                                (props.hidePremierePriceDiscount || !!reserveCouponId) ? null : (
                                    <div className="ticket__price">
                                        {section === 'best_available' ? getPriceRangeStringFromLevels() : getPriceStringFromLevelObject(getLevelById(section))}
                                    </div>
                                )
                             )}
                            {!is_ga && props.sectionPriceLevels.map((sectionLevel, index) => {
                                if (
                                    +sectionLevel?.price_level_id === 0 ||
                                    (!reserveCouponId && section !== 'best_available' && +section === +sectionLevel?.price_level_id) ||
                                    (!!reserveCouponId && validReservePlIds.includes(+sectionLevel?.price_level_id))
                                ) {
                                    let priceString = '';
                                    let disclosureSpan = '';
                                    if (reserveCouponId) {
                                        priceString = getDescriptionForSectionPriceLevel(sectionLevel?.discount_type, sectionLevel?.discount);
                                    } else {
                                        if (!props.hidePremierePriceDiscount) {
                                            if (section === 'best_available' && +sectionLevel?.price_level_id === 0) {
                                                const minLevel = getMinPricedPriceLevel();
                                                const maxLevel = getMaxPricedPriceLevel();
                                                if (!minLevel || !maxLevel) {
                                                    return null;
                                                }
                                                const minString = getDiscountedPriceForLevel(minLevel, sectionLevel, true);
                                                const maxString = getDiscountedPriceForLevel(maxLevel, sectionLevel, true);
                                                priceString = minString === maxString || minString === translate('checkout__free') ? minString : translate('checkout__low_thru_high', {lowAmount:minString, highAmount:maxString});
                                                if (props.discloseFee) {
                                                    const minFeeDisclosure = getDiscountedPriceForLevel(minLevel, sectionLevel);
                                                    const maxFeeDisclosure = getDiscountedPriceForLevel(maxLevel, sectionLevel);
                                                    if (minString === maxString || minString === translate('checkout__free')) {
                                                      if (minFeeDisclosure[1]) {
                                                        disclosureSpan = <span>{translate(props.feeVerbiage ? "checkout__amount_plus_amount_fees" : "checkout__amount_plus_amount_service_fee", {
                                                          amount:minFeeDisclosure[0],
                                                          feeAmount:minFeeDisclosure[1]
                                                        })}</span>;
                                                      } else {
                                                        disclosureSpan = <span> </span>;
                                                      }
                                                    } else {
                                                      if (minFeeDisclosure[1] && maxFeeDisclosure[1]) {
                                                        if (minFeeDisclosure[1] === maxFeeDisclosure[1]) {
                                                            disclosureSpan =
                                                              <span>
                                                                <span>({translate('checkout__low_thru_high', {
                                                                  lowAmount:minFeeDisclosure[0],
                                                                  highAmount:maxFeeDisclosure[0]
                                                                })},</span>
                                                                <div>{translate(props.feeVerbiage ? 'checkout__plus_word_fees' : 'checkout__plus_word_service_fee', {
                                                                  amount:maxFeeDisclosure[1]
                                                                })})</div>
                                                              </span>;
                                                          } else {
                                                            disclosureSpan =
                                                              <span>
                                                                <span>({translate('checkout__low_thru_high', {
                                                                  lowAmount: minFeeDisclosure[0],
                                                                  highAmount: maxFeeDisclosure[0]
                                                                })},</span>
                                                                <div>{translate(props.feeVerbiage ? "checkout__plus_word_range_fees" : "checkout__plus_word_range_service_fee", {
                                                                  lowAmount: minFeeDisclosure[1],
                                                                  highAmount: maxFeeDisclosure[1]
                                                                })})</div>
                                                              </span>;
                                                        }
                                                      } else if (!minFeeDisclosure[1] && maxFeeDisclosure[1]) {
                                                        disclosureSpan =
                                                          <span>
                                                            <span>({translate('checkout__low_thru_high', {
                                                              lowAmount:minFeeDisclosure[0],
                                                              highAmount:maxFeeDisclosure[0]
                                                            })},</span>
                                                            <div>{translate(props.feeVerbiage ? "checkout__plus_word_range_fees" : "checkout__plus_word_range_service_fee", {
                                                              lowAmount:formatCurrency(0),
                                                              highAmount:maxFeeDisclosure[1]
                                                            })})</div>
                                                          </span>;
                                                      }
                                                    }
                                                }

                                            } else {
                                                const priceLevelObject = getLevelById(section);
                                                if (!priceLevelObject) {
                                                    return null;
                                                }
                                                priceString = getDiscountedPriceForLevel(priceLevelObject, sectionLevel, true);
                                                if (props.discloseFee) {
                                                  const feeDisclosure = getDiscountedPriceForLevel(priceLevelObject, sectionLevel);
                                                  if (feeDisclosure[1]) {
                                                    disclosureSpan = <span>{translate(props.feeVerbiage ? "checkout__amount_plus_amount_fees" : "checkout__amount_plus_amount_service_fee", {
                                                      amount:feeDisclosure[0],
                                                      feeAmount:feeDisclosure[1]
                                                    })}</span>;
                                                  } else {
                                                    disclosureSpan = <span> </span>;
                                                  }
                                                }
                                            }
                                        }
                                    }


                                    const secQuantities = getQuantityOptions(sectionLevel);

                                    return (
                                        <PlComponent
                                            key={index}
                                            level={{
                                                name: sectionLevel?.name ?? translate('checkout__section'),
                                                price: {...priceObjectPlaceholder},
                                                service_fee: {...priceObjectPlaceholder},
                                            }}
                                            quantity={getSectionLevelQuantity(sectionLevel.id)}
                                            onIncrement={() => {setSectionLevelQuantityForId(sectionLevel.id, traverseQuantityOptions(secQuantities, getSectionLevelQuantity(sectionLevel.id), 1))}}
                                            onDecrement={() => {setSectionLevelQuantityForId(sectionLevel.id, Math.max(0, traverseQuantityOptions(secQuantities, getSectionLevelQuantity(sectionLevel.id), -1)))}}
                                            onQuantityInput={e => {setSectionLevelQuantityForId(sectionLevel.id, Math.max(0, +e.target.value))}}
                                            quantities={secQuantities}
                                            initialQuantities={secQuantities}
                                            disabled={!!props.disabled}
                                            isPWYW={false}
                                            customTicketInformationBelowName={(
                                              <div>
                                                  <div className="ticket__price--total hide-on-medium-screen">
                                                      {priceString}
                                                  </div>
                                                  {!!disclosureSpan && <div className="ticket__price--breakdown">
                                                      {disclosureSpan}
                                                  </div>}
                                              </div>
                                            )}
                                            customTicketInformationToRightOfName={(
                                                <div className="ticket__price--total">
                                                    {priceString}
                                                </div>
                                            )}
                                            customFeeDisplay={(
                                                <div className="ticket__price">
                                                    {priceString}
                                                </div>
                                            )}
                                        />
                                    );
                                }
                                return null;
                            })}
                        </div>
                    )}
                </div>
            </div>
    );

}

BestAvailableSeatPicker.propTypes = {
    priceLevels: PropTypes.object.isRequired,
    sectionPriceLevels: PropTypes.array.isRequired,
    sectionFeeMap: PropTypes.object.isRequired,
    quantities: PropTypes.object.isRequired,
    sectionLevelQuantities: PropTypes.object.isRequired,
    onSetQuantities: PropTypes.func.isRequired,
    onSetSectionLevelQuantities: PropTypes.func.isRequired,
    hideBestAvailable: PropTypes.bool,
    disableBestAvailable: PropTypes.bool,
    discloseFee: PropTypes.bool,
    purchaseLimit: PropTypes.number,
    incrementBy: PropTypes.number,
    defaultPriceLevelName: PropTypes.any,
    hidePremierePriceDiscount: PropTypes.bool,
    reserveCouponUsage: PropTypes.object,
    locale: PropTypes.string.isRequired,
    reservedPriceLevels: PropTypes.object,
    reservedQuantites: PropTypes.object,
    feeVerbiage: PropTypes.bool
};

BestAvailableSeatPicker.defaultProps = {
    hideBestAvailable: false,
    disableBestAvailable: false,
    discloseFee: false,
    purchaseLimit: 10,
    incrementBy: 1,
    defaultPriceLevelName: null,
    hidePremierePriceDiscount: false,
    reservedQuantites: {},
    feeVerbiage: false
}

export default BestAvailableSeatPicker;
